import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { remoteApi, calendarApi } from '../utils/api';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Laptop, AlertCircle, CheckCircle, Loader, Clock, MapPin,
  Sunrise, Sunset, Sun, Info, Shield, ExternalLink, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const PERIOD_OPTIONS = [
  {
    value: 'morning',
    icon: Sunrise,
    label: 'Solo mañana',
    sublabel: '0.5 días',
    time: '08:00 – 14:00',
    days: 0.5,
    gradient: 'from-amber-400 to-orange-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-300 dark:border-amber-700',
    ring: 'ring-amber-400',
    text: 'text-amber-700 dark:text-amber-400',
  },
  {
    value: 'full_day',
    icon: Sun,
    label: 'Día completo',
    sublabel: '1 día',
    time: '08:00 – 18:00',
    days: 1.0,
    gradient: 'from-brand to-green-400',
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-300 dark:border-green-700',
    ring: 'ring-brand',
    text: 'text-green-700 dark:text-green-400',
  },
  {
    value: 'afternoon',
    icon: Sunset,
    label: 'Solo tarde',
    sublabel: '0.5 días',
    time: '14:00 – 18:00',
    days: 0.5,
    gradient: 'from-blue-400 to-indigo-500',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-300 dark:border-blue-700',
    ring: 'ring-blue-400',
    text: 'text-blue-700 dark:text-blue-400',
  },
];

function isWeekend(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr + 'T12:00');
  return d.getDay() === 0 || d.getDay() === 6;
}

export default function RemoteWorkForm() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Telework enabled for all employees
  const canTelework = true;

  const [date, setDate] = useState('');
  const [period, setPeriod] = useState('full_day');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('18:00');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [monthlyBalance, setMonthlyBalance] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [dateHoliday, setDateHoliday] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const selectedOpt = PERIOD_OPTIONS.find(p => p.value === period);

  useEffect(() => {
    const yr = new Date().getFullYear();
    calendarApi.holidays(yr).then(r => setHolidays(r.data)).catch(console.error);
  }, []);

  // Reload monthly balance whenever the selected date changes month
  useEffect(() => {
    if (date) {
      const d = new Date(date + 'T12:00:00');
      remoteApi.monthlyBalance(d.getFullYear(), d.getMonth() + 1)
        .then(r => setMonthlyBalance(r.data)).catch(console.error);
    } else {
      // Load current month by default
      const now = new Date();
      remoteApi.monthlyBalance(now.getFullYear(), now.getMonth() + 1)
        .then(r => setMonthlyBalance(r.data)).catch(console.error);
    }
  }, [date]);

  useEffect(() => {
    if (date) {
      const h = holidays.find(h => h.date === date);
      setDateHoliday(h || null);
    }
  }, [date, holidays]);

  const handlePeriodChange = (val) => {
    setPeriod(val);
    const opt = PERIOD_OPTIONS.find(p => p.value === val);
    if (opt) {
      const times = opt.time.split(' – ');
      setStartTime(times[0]); setEndTime(times[1]);
    }
  };

  // Independent quota model: full days (max 6) and half-days (max 8) are separate
  const fullDaysAvail  = monthlyBalance?.full_days_available ?? 6;
  const halfDaysAvail  = monthlyBalance?.half_days_available ?? 8;
  const fullDaysUsed   = monthlyBalance?.full_days_used ?? 0;
  const halfDaysUsed   = monthlyBalance?.half_days_used ?? 0;

  const canSubmitBalance = period === 'full_day' ? fullDaysAvail > 0 : halfDaysAvail > 0;
  const canSubmit = date && !isWeekend(date) && !dateHoliday && canSubmitBalance;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await remoteApi.create({
        date,
        period_type: period,
        start_time: startTime || undefined,
        end_time: endTime || undefined,
        // location NOT sent from employee - detected by server silently
        notes: notes || undefined,
      });
      setSuccess(true);
      setTimeout(() => navigate('/my-requests'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear la solicitud');
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-card border border-border rounded-2xl p-10 text-center shadow-xl max-w-sm"
      >
        <motion.div
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 mx-auto mb-5 flex items-center justify-center shadow-lg"
        >
          <CheckCircle className="h-10 w-10 text-white" />
        </motion.div>
        <h2 className="text-xl font-bold font-heading mb-2">¡Teletrabajo registrado!</h2>
        <p className="text-sm text-muted-foreground">Tu día de teletrabajo ha sido aprobado automáticamente.</p>
        <div className="mt-4 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
          {date} · {selectedOpt.label}
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="max-w-3xl">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 shadow-md">
            <Laptop className="h-5 w-5 text-white" />
          </div>
          <h1 className="text-2xl font-bold font-heading">{t('newRemote')}</h1>
        </div>
        <p className="text-sm text-muted-foreground ml-12">Solicita un día de teletrabajo para tu equipo</p>
      </motion.div>

      {/* Balance bar — monthly */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-card border border-border rounded-2xl p-4 mb-5 shadow-sm"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Teletrabajo este mes</span>
          <span className="text-sm font-bold text-brand">
            {period === 'full_day'
              ? `${fullDaysAvail} de 6 días disponibles`
              : `${halfDaysAvail} de 8 medias jornadas disponibles`
            }
          </span>
        </div>
        <div className="h-2.5 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, (fullDaysUsed / 6) * 100)}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        </div>
        <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
          <span>{fullDaysUsed} días completos usados este mes</span>
          <span className={fullDaysAvail === 0 ? 'text-red-500 font-semibold' : ''}>{fullDaysAvail} de 6 disponibles</span>
        </div>
        {halfDaysUsed > 0 && (
          <>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden mt-1">
              <motion.div
                className="h-full bg-gradient-to-r from-purple-400 to-pink-400 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(100, (halfDaysUsed / 8) * 100)}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-muted-foreground">
              <span>{halfDaysUsed} medias jornadas usadas este mes</span>
              <span className={halfDaysAvail === 0 ? 'text-red-500 font-semibold' : ''}>{halfDaysAvail} de 8 disponibles</span>
            </div>
          </>
        )}
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
        {/* LEFT: Form */}
        <motion.form
          initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}
          onSubmit={handleSubmit}
          className="md:col-span-3 bg-card border border-border rounded-2xl p-6 shadow-sm space-y-6"
        >
          {/* Period selector */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Selecciona el tramo *</Label>
            <div className="grid grid-cols-3 gap-2">
              {PERIOD_OPTIONS.map((opt) => {
                const isActive = period === opt.value;
                return (
                  <motion.button
                    key={opt.value}
                    type="button"
                    onClick={() => handlePeriodChange(opt.value)}
                    data-testid={`period-${opt.value}`}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 overflow-hidden ${
                      isActive
                        ? `${opt.border} ${opt.bg} ring-2 ${opt.ring} shadow-sm`
                        : 'border-border hover:border-brand/40 hover:bg-muted/30'
                    }`}
                  >
                    {isActive && (
                      <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 0.08 }}
                        className={`absolute inset-0 bg-gradient-to-br ${opt.gradient}`}
                      />
                    )}
                    <div className="relative">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${opt.gradient} flex items-center justify-center mb-2 shadow-sm`}>
                        <opt.icon className="h-4 w-4 text-white" />
                      </div>
                      <div className={`text-xs font-semibold ${isActive ? opt.text : 'text-foreground'}`}>{opt.label}</div>
                      <div className="text-xs text-muted-foreground">{opt.sublabel}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 font-mono">{opt.time}</div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Date */}
          <div className="space-y-1.5">
            <Label htmlFor="remote-date" className="text-sm font-medium flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-blue-500" />Fecha *
            </Label>
            <Input
              id="remote-date" type="date" value={date}
              min={new Date().toISOString().split('T')[0]}
              onChange={e => setDate(e.target.value)}
              required data-testid="remote-date"
              className="focus-visible:ring-blue-400 h-10"
            />
            <AnimatePresence>
              {date && isWeekend(date) && (
                <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="text-xs text-destructive flex items-center gap-1 mt-1">
                  <AlertCircle className="h-3.5 w-3.5" />Este día es fin de semana
                </motion.p>
              )}
              {dateHoliday && (
                <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="text-xs text-orange-600 flex items-center gap-1 mt-1">
                  <AlertCircle className="h-3.5 w-3.5" />Festivo: {dateHoliday.name}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {/* Times */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="start-time" className="text-sm font-medium">Hora inicio</Label>
              <Input id="start-time" type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                data-testid="remote-start-time" className="focus-visible:ring-blue-400 font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-time" className="text-sm font-medium">Hora fin</Label>
              <Input id="end-time" type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                data-testid="remote-end-time" className="focus-visible:ring-blue-400 font-mono" />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="remote-notes" className="text-sm font-medium">{t('notes')} (opcional)</Label>
            <Textarea id="remote-notes" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Comentario adicional..." rows={2}
              data-testid="remote-notes" className="resize-none focus-visible:ring-blue-400" />
          </div>

          {/* IP location notice removed */}

          {!canSubmitBalance && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="flex items-start gap-2 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>Saldo insuficiente. {period === 'full_day'
                ? `Días completos disponibles: ${fullDaysAvail} de 6`
                : `Medias jornadas disponibles: ${halfDaysAvail} de 8`
              }</span>
            </motion.div>
          )}

          {error && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="flex items-start gap-2 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-sm text-destructive"
              data-testid="remote-error">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}

          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancelar</Button>
            <motion.div className="flex-1" whileHover={{ scale: canSubmit ? 1.01 : 1 }} whileTap={{ scale: canSubmit ? 0.99 : 1 }}>
              <Button
                type="submit" disabled={loading || !canSubmit}
                data-testid="remote-submit"
                className="w-full h-11 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-semibold shadow-md"
              >
                {loading
                  ? <><Loader className="h-4 w-4 animate-spin mr-2" />Enviando...</>
                  : <><Laptop className="h-4 w-4 mr-2" />Informar teletrabajo</>
                }
              </Button>
            </motion.div>
          </div>
        </motion.form>

        {/* RIGHT: Summary */}
        <motion.div
          initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
          className="md:col-span-2 space-y-4"
        >
          {/* Visual period summary */}
          <div className={`rounded-2xl p-6 ${selectedOpt.bg} border ${selectedOpt.border} text-center transition-all duration-300`}>
            <motion.div
              key={period}
              initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${selectedOpt.gradient} flex items-center justify-center mx-auto mb-3 shadow-lg`}
            >
              <selectedOpt.icon className="h-7 w-7 text-white" />
            </motion.div>
            <div className={`text-4xl font-bold font-heading ${selectedOpt.text}`}>
              {selectedOpt.days}
            </div>
            <div className="text-sm text-muted-foreground mt-1">{selectedOpt.label}</div>
            <div className="text-xs text-muted-foreground font-mono mt-1">{selectedOpt.time}</div>
          </div>

          {/* Date preview */}
          <AnimatePresence>
            {date && !isWeekend(date) && !dateHoliday && (
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                className="bg-card border border-border rounded-2xl p-4 shadow-sm text-center"
              >
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Fecha solicitada</div>
                <div className="text-lg font-bold font-heading capitalize">
                  {new Date(date + 'T12:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Policy info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3">
            <div className="flex items-start gap-2">
              <Info className="h-3.5 w-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                <p className="font-medium">Política de teletrabajo</p>
                <p>Días completos: máximo 6 por mes — cuota independiente</p>
                <p>Medias jornadas (mañana/tarde): máximo 8 por mes — cuota independiente</p>
                <p>Aprobación automática</p>
              </div>
            </div>
          </div>

          {/* Remaining balance after */}
          {monthlyBalance && (
            <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
              <div className="text-xs font-medium text-muted-foreground mb-2">Saldo mensual tras solicitud</div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-28">Días completos</span>
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${Math.min(100, ((fullDaysUsed + (period === 'full_day' ? 1 : 0)) / 6) * 100)}%`, transition: 'width 0.4s' }}
                    />
                  </div>
                  <span className="text-xs font-bold text-blue-600 w-16 text-right">
                    {Math.max(0, 6 - fullDaysUsed - (period === 'full_day' ? 1 : 0))} de 6 libres
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-28">Medias jornadas</span>
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-purple-400 rounded-full"
                      style={{ width: `${Math.min(100, ((halfDaysUsed + (period !== 'full_day' ? 1 : 0)) / 8) * 100)}%`, transition: 'width 0.4s' }}
                    />
                  </div>
                  <span className="text-xs font-bold text-purple-600 w-16 text-right">
                    {Math.max(0, 8 - halfDaysUsed - (period !== 'full_day' ? 1 : 0))} de 8 libres
                  </span>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
